import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PublicBanner } from "@/hooks/useRestaurantBanners";
import { Hand } from "lucide-react";

interface TabletScreensaverProps {
  banners: PublicBanner[];
  intervalSeconds: number;
  onWakeUp: () => void;
  restaurantName?: string;
  restaurantLogo?: string | null;
}

export function TabletScreensaver({
  banners,
  intervalSeconds,
  onWakeUp,
  restaurantName,
  restaurantLogo,
}: TabletScreensaverProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (banners.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
    }, intervalSeconds * 1000);

    return () => clearInterval(interval);
  }, [banners.length, intervalSeconds]);

  const handleClick = () => {
    onWakeUp();
  };

  // If no banners, show a default screen
  if (banners.length === 0) {
    return (
      <div
        className="fixed inset-0 z-50 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center cursor-pointer"
        onClick={handleClick}
      >
        {restaurantLogo && (
          <img
            src={restaurantLogo}
            alt={restaurantName}
            className="w-32 h-32 rounded-full object-cover mb-8 ring-4 ring-white/20"
          />
        )}
        <h1 className="text-4xl font-bold text-white mb-4">{restaurantName}</h1>
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center gap-4 mt-12"
        >
          <Hand className="w-16 h-16 text-white/80" />
          <p className="text-2xl text-white/80 font-medium">Toque para pedir</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black cursor-pointer overflow-hidden"
      onClick={handleClick}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0"
        >
          {/* Banner image */}
          <img
            src={banners[currentIndex].image_url}
            alt={banners[currentIndex].title || "Promoção"}
            className="w-full h-full object-cover"
          />

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />

          {/* Content overlay */}
          <div className="absolute inset-0 flex flex-col justify-end p-12">
            {banners[currentIndex].title && (
              <motion.h2
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-5xl font-bold text-white mb-4"
              >
                {banners[currentIndex].title}
              </motion.h2>
            )}
            {banners[currentIndex].subtitle && (
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-2xl text-white/90 mb-8"
              >
                {banners[currentIndex].subtitle}
              </motion.p>
            )}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="inline-flex items-center gap-3 bg-primary px-8 py-4 rounded-xl w-fit"
            >
              <Hand className="w-8 h-8 text-primary-foreground" />
              <span className="text-2xl font-semibold text-primary-foreground">
                {banners[currentIndex].cta_text || "TOQUE PARA PEDIR"}
              </span>
            </motion.div>
          </div>

          {/* Restaurant logo */}
          {restaurantLogo && (
            <div className="absolute top-8 left-8">
              <img
                src={restaurantLogo}
                alt={restaurantName}
                className="w-20 h-20 rounded-full object-cover ring-2 ring-white/50"
              />
            </div>
          )}

          {/* Pagination dots */}
          {banners.length > 1 && (
            <div className="absolute bottom-8 right-8 flex gap-2">
              {banners.map((_, index) => (
                <div
                  key={index}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentIndex
                      ? "bg-white scale-125"
                      : "bg-white/40"
                  }`}
                />
              ))}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
